/**
 * TemplateBands — runtime resolver that pulls the admin-editable dynamic
 * template for a given entity (providers | services | categories | locations |
 * project_requests | blog_posts | help_articles) and renders any *extra* sections
 * the admin has added — as pre-content or post-content bands around the built-in
 * flagship layout.
 *
 * The built-in layouts remain the source of truth for transaction-critical UI.
 * Sections in a template whose `slot` is `"header"` render *above* the main
 * content; sections whose `slot` is `"footer"` render *below*. Any block kind
 * unknown to blockRegistry (e.g. `provider_hero`, `service_packages` — those are
 * baked into the detail page itself) is silently skipped so the built-in
 * flagship layout keeps rendering the transactional UI.
 */
import React, { useEffect, useState } from "react";
import api from "@/lib/api";
import { SectionRenderer, BLOCK_REGISTRY } from "@/builder/blockRegistry";

function isRenderableBlock(block) {
  const cfg = BLOCK_REGISTRY[block?.kind];
  return !!cfg;
}

function pickBand(sections, slot) {
  // A section is a header/footer band if:
  //   • its `slot` === slot, OR
  //   • it contains ONLY blocks whose kinds live in BLOCK_REGISTRY (indicates
  //     admin-added freeform content, not baked-in placeholders).
  const bySlot = (sections || []).filter((s) => s?.slot === slot);
  if (bySlot.length > 0) return bySlot;
  if (slot !== "footer") return [];
  // Fallback: no explicit slot — treat any section with only registry-known
  // blocks as an appended freeform band (rendered after built-in content).
  return (sections || []).filter((s) =>
    (s?.blocks || []).length > 0 && (s.blocks || []).every(isRenderableBlock)
  );
}

export default function TemplateBands({ entity, slot = "header" }) {
  const [sections, setSections] = useState([]);
  useEffect(() => {
    let alive = true;
    api
      .get(`/templates/${entity}`)
      .then((r) => {
        if (!alive) return;
        setSections(pickBand(r.data?.sections || [], slot));
      })
      .catch(() => { /* no template → nothing to render, leave built-in as-is */ });
    return () => { alive = false; };
  }, [entity, slot]);
  if (!sections || sections.length === 0) return null;
  return (
    <div data-testid={`tpl-band-${entity}-${slot}`}>
      {sections.map((s) => {
        // Filter to registry-renderable blocks only.
        const safe = { ...s, blocks: (s.blocks || []).filter(isRenderableBlock) };
        if (safe.blocks.length === 0) return null;
        return <SectionRenderer key={s.id} section={safe} mode="view" />;
      })}
    </div>
  );
}
